"use client";
import { FC, useMemo } from "react";
import cn from "classnames";
import { useSearchParams, usePathname } from "next/navigation";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CaretDownLine, CheckMarkThin } from "@/icons";
import Link from "@/components/Link";

export type TopFilterTab = {
  label: string;
  filterGroup: string;
  dlt: string;
  dld: string;
};

type SWDCInternalSearchTopFilterProps = {
  tabs?: TopFilterTab[];
};

export const SWDCInternalSearchTopFilter: FC<
  SWDCInternalSearchTopFilterProps
> = ({ tabs: tabsProp }) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const tabs = useMemo(() => tabsProp || [], [tabsProp]);

  const activeTab = useMemo(() => {
    const pathSegments = pathname?.split("/").filter(Boolean) || [];
    const lastSegment = pathSegments[pathSegments.length - 1];

    let activeFilterGroup = "products"; // default
    if (
      lastSegment &&
      ["resources", "government", "everything"].includes(lastSegment)
    ) {
      activeFilterGroup = lastSegment;
    }

    const tabIndex = tabs.findIndex(
      (tab) => tab.filterGroup === activeFilterGroup,
    );

    return tabIndex >= 0 ? tabIndex : 0;
  }, [pathname, tabs]);

  const getTabHref = (filterGroup: string) => {
    const basePath = "/internalsearch/search";
    const newPath =
      filterGroup === "products" ? basePath : `${basePath}/${filterGroup}`;

    const params = new URLSearchParams(searchParams?.toString() || "");
    params.delete("c");
    params.delete("page");

    const queryString = params.toString();
    return queryString ? `${newPath}?${queryString}` : newPath;
  };

  return (
    <div data-automation-id="search-top-filter">
      <Listbox value={activeTab} onChange={() => {}}>
        <div className="relative block w-full md:hidden">
          <ListboxButton
            className={cn(
              "border-swdc-gray-200 text-subhead-2xs sm:text-subhead-s flex w-full min-w-0 cursor-pointer flex-row-reverse items-center justify-between border border-solid px-4 pt-3.5 pb-4 text-left leading-7 font-medium sm:leading-8",
              "bg-swdc-cool-gray-100 [&_svg]:text-swdc-black-600 text-swdc-blue-500",
            )}
          >
            <CaretDownLine fill="currentColor" className="shrink-0" />
            <span className="mr-2 truncate overflow-hidden">
              {tabs[activeTab]?.label}
            </span>
          </ListboxButton>

          <ListboxOptions
            className={cn(
              "border-swdc-gray-200 text-body-m absolute inset-x-0 z-10 mt-3 w-full border border-solid font-medium shadow-[0_20px_40px_0_rgba(0,0,0,0.2)] focus:outline-none",
              "bg-swdc-cool-gray-100",
            )}
            as="ul"
          >
            {tabs?.map((tab, index) => (
              <ListboxOption
                data-linktype={tab.dlt}
                data-linkdetail={tab.dld}
                key={`${tab.label}-${index}`}
                as={Link}
                href={getTabHref(tab.filterGroup)}
                scroll={false}
                className={cn(
                  "data-focus:outline-swdc-blue-400 relative flex w-full cursor-pointer py-2 pr-12 pl-4 text-left not-data-focus:outline-none hover:outline-none focus:outline-none focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-selected:outline-none",
                  activeTab === index
                    ? "bg-swdc-silver-300"
                    : "bg-swdc-cool-gray-100",
                )}
                value={index}
              >
                {({ selected }) => (
                  <span
                    className={cn(
                      "flex w-full",
                      selected ? "font-medium" : "font-normal",
                    )}
                  >
                    {selected && (
                      <CheckMarkThin
                        className="absolute top-1/2 right-4 w-5 -translate-y-1/2 lg:w-6"
                        fill="currentColor"
                      />
                    )}
                    {tab.label}
                  </span>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      <ul className="hidden md:flex md:flex-wrap md:items-center md:gap-x-16 md:gap-y-4">
        {tabs.map((tab, index) => (
          <li
            key={`${tab.label}-${index}`}
            className={cn(
              "group after:border-r-swdc-silver-300 relative whitespace-nowrap after:absolute after:top-1/2 after:-right-8 after:h-6 after:-translate-y-1/2 after:border-r-2 last:after:border-r-0",
              activeTab === index
                ? "text-headline"
                : "text-link hover:text-link-hover active:text-link-pressed",
            )}
          >
            <Link
              href={getTabHref(tab.filterGroup)}
              data-linktype={tab.dlt}
              data-linkdetail={tab.dld}
              data-automation-id={`top-filter-${tab.filterGroup}`}
              scroll={false}
              className={cn(
                "text-link-m relative block cursor-pointer pb-1.5 font-bold",
                activeTab === index
                  ? "text-headline"
                  : "text-link hover:text-link-hover active:text-link-pressed",
              )}
            >
              {tab.label}
              <div
                className={cn(
                  "bg-swdc-orange-400 absolute right-0 -bottom-1 left-0 h-0 rounded-full group-hover:h-0.5",
                  activeTab === index
                    ? "h-1 group-hover:-bottom-1 group-hover:h-1"
                    : "mt-0.5",
                )}
              ></div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};
